import React, { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Lock, LogOut, ArrowLeft, Phone, MapPin, Car, Clock, User, FileText, Bell, MessageCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_ADMIN = "19362498196";

export default function AdminPanel({ onBack }) {
  const { t } = useLanguage();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [requests, setRequests] = useState([]);
  const [loggingIn, setLoggingIn] = useState(false);
  const [newCount, setNewCount] = useState(0);

  useEffect(() => {
    checkAuth();
  }, []);

  // Poll for new requests every 15 seconds
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(() => {
      loadRequests(true);
    }, 15000);
    return () => clearInterval(interval);
  }, [user, requests.length]);

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
      loadRequests();
    } catch {
      setUser(null);
    }
    setLoading(false);
  };

  const loadRequests = async (silent = false) => {
    try {
      const { data } = await axios.get(`${API}/admin/service-requests`, { withCredentials: true });
      if (silent && data.length > requests.length) {
        const diff = data.length - requests.length;
        setNewCount(diff);
        // Browser notification
        if ("Notification" in window && Notification.permission === "granted") {
          new Notification(`${diff} nueva(s) solicitud(es)`, { body: "Houston Pro Locksmith" });
        }
      }
      setRequests(data);
    } catch {
      // ignore
    }
  };

  const enableNotifications = () => {
    if ("Notification" in window) {
      Notification.requestPermission();
    }
  };

  const updateStatus = async (requestId, newStatus) => {
    try {
      await axios.patch(`${API}/admin/service-requests/${requestId}`, { status: newStatus }, { withCredentials: true });
      setRequests((prev) =>
        prev.map((r) => (r.id === requestId ? { ...r, status: newStatus } : r))
      );
    } catch {
      // ignore
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, loginForm, { withCredentials: true });
      setUser(data);
      loadRequests();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setLoginError(typeof detail === "string" ? detail : "Login failed");
    }
    setLoggingIn(false);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      // ignore
    }
    setUser(null);
    setRequests([]);
  };

  const openWhatsApp = (req) => {
    const msg = `Re: ${req.name} - ${req.problem_type}%0APhone: ${req.phone}%0ALocation: ${req.location}${req.vehicle_model ? '%0AVehicle: ' + req.vehicle_model : ''}${req.description ? '%0ADetails: ' + req.description : ''}`;
    window.open(`https://wa.me/${req.phone.replace(/\D/g, '')}?text=${msg}`, "_blank");
  };

  const statusColor = {
    pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
    contacted: "bg-blue-100 text-blue-800 border-blue-200",
    completed: "bg-green-100 text-green-800 border-green-200",
  };

  const statusLabels = {
    pending: { en: "Pending", es: "Pendiente" },
    contacted: { en: "Contacted", es: "Contactado" },
    completed: { en: "Completed", es: "Completado" },
  };

  const typeColors = {
    emergency: "text-[#C1121F] bg-red-50",
    residential: "text-[#0B132B] bg-gray-50",
    commercial: "text-[#0B132B] bg-gray-50",
    automotive: "text-[#D4AF37] bg-amber-50",
    safes: "text-[#0B132B] bg-gray-50",
    security: "text-[#0B132B] bg-gray-50",
  };

  const { lang } = useLanguage();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center px-6">
        <div className="w-full max-w-sm">
          <button
            data-testid="admin-back-btn"
            onClick={onBack}
            className="flex items-center gap-2 text-sm text-[#495057] hover:text-[#0B132B] mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t.admin.backToSite}
          </button>

          <div className="bg-white border border-[#E9ECEF] rounded-sm p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#0B132B] rounded-sm flex items-center justify-center">
                <Lock className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <h1 className="font-heading text-xl font-bold text-[#0B132B]">{t.admin.login}</h1>
            </div>

            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#0B132B] mb-1.5">{t.admin.email}</label>
                <input
                  data-testid="admin-email"
                  type="email"
                  required
                  value={loginForm.email}
                  onChange={(e) => setLoginForm((p) => ({ ...p, email: e.target.value }))}
                  className="admin-input w-full h-11 px-3 rounded-md border border-[#E9ECEF] focus:outline-none focus:ring-2 focus:ring-[#D4AF37] text-base"
                  autoComplete="email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0B132B] mb-1.5">{t.admin.password}</label>
                <input
                  data-testid="admin-password"
                  type="password"
                  required
                  value={loginForm.password}
                  onChange={(e) => setLoginForm((p) => ({ ...p, password: e.target.value }))}
                  className="admin-input w-full h-11 px-3 rounded-md border border-[#E9ECEF] focus:outline-none focus:ring-2 focus:ring-[#D4AF37] text-base"
                  autoComplete="current-password"
                />
              </div>

              {loginError && (
                <p data-testid="admin-login-error" className="text-sm text-red-600">{loginError}</p>
              )}

              <button
                data-testid="admin-login-btn"
                type="submit"
                disabled={loggingIn}
                className="w-full bg-[#0B132B] text-white py-3 text-sm font-semibold rounded-sm hover:bg-[#0B132B]/90 transition-colors disabled:opacity-60"
              >
                {loggingIn ? "..." : t.admin.loginBtn}
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  const pendingCount = requests.filter((r) => r.status === "pending").length;

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Header */}
      <header className="bg-[#0B132B] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-sm bg-[#D4AF37] flex items-center justify-center">
              <Lock className="w-4 h-4 text-[#0B132B]" />
            </div>
            <h1 className="font-heading text-lg font-semibold">{t.admin.dashboard}</h1>
            {pendingCount > 0 && (
              <span className="bg-[#C1121F] text-white text-xs px-2 py-0.5 rounded-full font-semibold">
                {pendingCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-4">
            <button
              data-testid="enable-notifications-btn"
              onClick={enableNotifications}
              className="text-sm text-white/60 hover:text-white transition-colors flex items-center gap-1"
              title="Enable browser notifications"
            >
              <Bell className="w-3.5 h-3.5" />
            </button>
            <button
              data-testid="admin-back-to-site"
              onClick={onBack}
              className="text-sm text-white/60 hover:text-white transition-colors flex items-center gap-1"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              {t.admin.backToSite}
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={handleLogout}
              className="text-sm text-white/60 hover:text-white transition-colors flex items-center gap-1"
            >
              <LogOut className="w-3.5 h-3.5" />
              {t.admin.logout}
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
        {/* Stats bar */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white border border-[#E9ECEF] rounded-sm p-4 text-center">
            <p className="text-2xl font-heading font-bold text-[#0B132B]">{requests.length}</p>
            <p className="text-xs text-[#495057]">Total</p>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-sm p-4 text-center">
            <p className="text-2xl font-heading font-bold text-yellow-800">{pendingCount}</p>
            <p className="text-xs text-yellow-700">{lang === "es" ? "Pendientes" : "Pending"}</p>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-sm p-4 text-center">
            <p className="text-2xl font-heading font-bold text-green-800">{requests.filter((r) => r.status === "completed").length}</p>
            <p className="text-xs text-green-700">{lang === "es" ? "Completados" : "Completed"}</p>
          </div>
        </div>

        <div className="mb-6 flex items-center justify-between">
          <p className="text-sm text-[#495057]">
            {requests.length} {requests.length === 1 ? "request" : "requests"}
          </p>
          <button
            data-testid="refresh-requests-btn"
            onClick={() => loadRequests()}
            className="text-sm text-[#D4AF37] hover:text-[#B5952F] font-medium transition-colors"
          >
            Refresh
          </button>
        </div>

        {requests.length === 0 ? (
          <div className="bg-white border border-[#E9ECEF] rounded-sm p-12 text-center">
            <FileText className="w-8 h-8 text-[#E9ECEF] mx-auto mb-3" />
            <p className="text-[#495057]">{t.admin.noRequests}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {requests.map((req) => (
              <div
                key={req.id}
                data-testid={`request-card-${req.id}`}
                className={`bg-white border rounded-sm p-5 hover:shadow-md transition-shadow ${
                  req.status === "pending" ? "border-yellow-200 border-l-4 border-l-yellow-400" : "border-[#E9ECEF]"
                }`}
              >
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="font-semibold text-[#0B132B] flex items-center gap-2">
                        <User className="w-4 h-4 text-[#495057]" />
                        {req.name}
                      </h3>
                      <span className={`text-xs px-2 py-0.5 rounded-sm font-medium ${typeColors[req.problem_type] || "text-[#0B132B] bg-gray-50"}`}>
                        {req.problem_type?.toUpperCase()}
                      </span>
                    </div>

                    <div className="flex flex-wrap gap-4 text-sm text-[#495057]">
                      <span className="flex items-center gap-1">
                        <Phone className="w-3.5 h-3.5" />
                        <a href={`tel:${req.phone}`} className="hover:text-[#0B132B] font-medium">{req.phone}</a>
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5" />
                        {req.location}
                      </span>
                      {req.vehicle_model && (
                        <span className="flex items-center gap-1">
                          <Car className="w-3.5 h-3.5" />
                          {req.vehicle_model}
                        </span>
                      )}
                    </div>

                    {req.description && (
                      <p className="text-sm text-[#495057] mt-1 bg-[#F8F9FA] px-3 py-2 rounded-sm">{req.description}</p>
                    )}
                  </div>

                  {/* Right side: status + actions */}
                  <div className="flex flex-col items-end gap-3 flex-shrink-0">
                    <div className="flex items-center gap-2 text-xs text-[#495057]">
                      <Clock className="w-3.5 h-3.5" />
                      {new Date(req.created_at).toLocaleString()}
                    </div>

                    {/* Status dropdown */}
                    <Select
                      value={req.status}
                      onValueChange={(val) => updateStatus(req.id, val)}
                    >
                      <SelectTrigger
                        data-testid={`status-select-${req.id}`}
                        className={`w-[160px] h-9 text-xs font-medium border ${statusColor[req.status] || statusColor.pending}`}
                      >
                        <SelectValue>
                          {statusLabels[req.status]?.[lang] || req.status}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending" data-testid={`status-pending-${req.id}`}>
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-yellow-500" />
                            {statusLabels.pending[lang]}
                          </span>
                        </SelectItem>
                        <SelectItem value="contacted" data-testid={`status-contacted-${req.id}`}>
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-blue-500" />
                            {statusLabels.contacted[lang]}
                          </span>
                        </SelectItem>
                        <SelectItem value="completed" data-testid={`status-completed-${req.id}`}>
                          <span className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-green-500" />
                            {statusLabels.completed[lang]}
                          </span>
                        </SelectItem>
                      </SelectContent>
                    </Select>

                    {/* Quick actions */}
                    <div className="flex items-center gap-2">
                      <a
                        data-testid={`call-client-${req.id}`}
                        href={`tel:${req.phone}`}
                        className="flex items-center gap-1 text-xs text-white bg-[#0B132B] px-3 py-1.5 rounded-sm hover:bg-[#0B132B]/80 transition-colors"
                      >
                        <Phone className="w-3 h-3" />
                        {lang === "es" ? "Llamar" : "Call"}
                      </a>
                      <button
                        data-testid={`whatsapp-client-${req.id}`}
                        onClick={() => openWhatsApp(req)}
                        className="flex items-center gap-1 text-xs text-white bg-[#25D366] px-3 py-1.5 rounded-sm hover:bg-[#1da851] transition-colors"
                      >
                        <MessageCircle className="w-3 h-3" />
                        WhatsApp
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
